/**
 * Page Performance → Overview: the health strip, four figure tiles with
 * their compare deltas and limits, the load-time chart, "At a glance"
 * (every count with what it is judged against), the slowest pages and
 * "What is recorded / never recorded".
 */
import { useMemo } from 'react'
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  FileSearch,
  Gauge,
  LayoutGrid,
  ShieldCheck,
  Timer,
  Users,
  XCircle,
} from 'lucide-react'
import { DataTable } from '../table/DataTable'
import { extractApiError } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import { fetchAllPages, useOverview, usePages, viewerTimeZone } from '../../lib/page-perf/api-client'
import {
  STATUSES,
  STATUS_LABEL,
  compareShort,
  countOf,
  deltaOf,
  formatLimitSeconds,
  formatMs,
  formatPercent,
  levelFor,
  limitPerHundredText,
  limitTriple,
  normaliseLimits,
  perHundredText,
  periodLabel,
  scopePhrase,
  slotUnit,
  usersExactFrom,
} from '../../lib/page-perf/view-model'
import { buildPageColumns } from './perf-columns'
import { LoadTimeChart } from './LoadTimeChart'
import {
  DeltaLine,
  FigureTile,
  FilterSpinner,
  InfoDot,
  PanelEmpty,
  PanelError,
  PanelLoading,
  PerfCard,
  PerfStatusBadge,
  ToneDot,
} from './PerfUi'
import { tintOf, toneOf } from './perf-tones'
import { usePerfTable } from './use-perf-table'

const isNum = (v) => typeof v === 'number' && Number.isFinite(v)

/* ── Health strip ──────────────────────────────────────────────────────── */

function HealthStrip({ health, limits, users, usersPrev, usersExact, compare, onStatus, onPages, onUsers, usersTarget }) {
  const counts = STATUSES.map((s) => ({ s, n: health?.[s] ?? 0 }))
  const sum = counts.reduce((a, c) => a + c.n, 0)
  const measured = isNum(health?.pages) ? health.pages : sum
  const known = isNum(health?.notOpened) ? measured + health.notOpened : null
  // The counts are worst-of-four (load time P95, TTFB, LCP, errors), so a
  // chip must not claim a single measure — the line under the chips spells
  // the four out with the load-time limits.
  const chipNote = {
    healthy: 'inside every limit',
    warning: 'worst measure over its warning limit',
    slow: 'worst measure over its slow limit',
    critical: 'worst measure over its critical limit',
  }
  const usersDelta = deltaOf(users, usersPrev, { neutral: true })
  return (
    <div className="grid min-w-0 grid-cols-1 gap-4 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 sm:p-5 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)_minmax(0,200px)] lg:items-center">
      <button type="button" onClick={onPages} className="flex min-w-0 cursor-pointer items-center gap-3 rounded-lg text-left hover:opacity-90" title="Open the Pages tab">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]">
          <LayoutGrid size={18} />
        </span>
        <span className="min-w-0">
          <span className="block font-display text-2xl font-semibold tabular-nums text-[color:var(--color-ink-strong)]">
            {formatNumber(measured)}
            {known != null ? <small className="text-base text-[color:var(--color-ink-muted)]"> / {formatNumber(known)}</small> : null}
          </span>
          <span className="block text-xs text-[color:var(--color-ink-muted)]">
            {measured === 1 ? 'page' : 'pages'} opened in this period · click a status to filter the Pages tab
          </span>
        </span>
      </button>
      <div className="min-w-0">
        <div
          className="flex h-2.5 w-full overflow-hidden rounded-full bg-[color:var(--color-rule)]"
          role="img"
          aria-label={counts.map((c) => `${STATUS_LABEL[c.s]} ${c.n}`).join(' · ')}
          title={counts.map((c) => `${STATUS_LABEL[c.s]} ${c.n}`).join(' · ')}
        >
          {sum > 0 ? counts.map((c) => <span key={c.s} style={{ width: `${(c.n / sum) * 100}%`, backgroundColor: toneOf(c.s) }} />) : null}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {counts.map((c) => (
            <button
              key={c.s}
              type="button"
              onClick={() => onStatus(c.s)}
              title={`Pages tab, ${STATUS_LABEL[c.s]} only — a page's colour is the worst of its P95, TTFB, LCP and failed requests`}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[color:var(--color-rule)] px-3 py-1 text-[13px] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]"
              style={{ backgroundColor: c.n > 0 ? tintOf(toneOf(c.s), 8) : undefined }}
            >
              <ToneDot tone={toneOf(c.s)} />
              {STATUS_LABEL[c.s]} <b className="tabular-nums">{formatNumber(c.n)}</b>
              {chipNote[c.s] ? <span className="text-xs text-[color:var(--color-ink-muted)]">{chipNote[c.s]}</span> : null}
            </button>
          ))}
        </div>
        <p className="m-0 mt-2 text-xs text-[color:var(--color-ink-muted)]">
          Each page counts once, by the worst of its load time (P95
          {limits.load ? ` — warning over ${formatLimitSeconds(limits.load.warning)}, slow over ${formatLimitSeconds(limits.load.slow)}, critical over ${formatLimitSeconds(limits.load.critical)}` : ''}),
          TTFB, LCP and errors, each against its own limits.
        </p>
      </div>
      <button
        type="button"
        onClick={onUsers}
        title={`${usersExact ? 'Distinct people' : 'People per day, added up'} — hashed per day, never identified. Opens the ${usersTarget} tab.`}
        className="min-w-0 cursor-pointer rounded-lg border border-[color:var(--color-rule)] px-3 py-2 text-left hover:bg-[color:var(--color-card-hover)]"
      >
        <span className="flex items-center gap-2">
          <Users size={15} className="text-[color:var(--color-accent-strong)]" />
          <b className="font-display text-xl tabular-nums text-[color:var(--color-ink-strong)]">{isNum(users) ? formatNumber(users) : '—'}</b>
        </span>
        <span className="block text-xs text-[color:var(--color-ink-muted)]">
          {users === 1 ? 'person' : 'people'} opened pages{usersExact ? '' : ' (per day, added up)'}
        </span>
        <span className="block text-xs text-[color:var(--color-ink-muted)]">
          <DeltaLine delta={usersDelta} vs={`${isNum(usersPrev) ? formatNumber(usersPrev) : '—'} in ${compareShort(compare)}`} />
        </span>
      </button>
    </div>
  )
}

/* ── At a glance ───────────────────────────────────────────────────────── */

function GlanceRow({ icon: Icon, tone, label, meaning, value, badge, link }) {
  return (
    <div className="flex min-w-0 items-start gap-3 border-b border-[color:var(--color-rule)] py-3 last:border-b-0">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: tintOf(tone, 16), color: tone }}>
        <Icon size={15} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-sm font-semibold text-[color:var(--color-ink-strong)]">{label}</p>
        <p className="m-0 mt-0.5 text-xs leading-snug text-[color:var(--color-ink-muted)]">{meaning}</p>
        {link}
      </div>
      <div className="shrink-0 text-right">
        <p className="m-0 font-display text-lg font-semibold tabular-nums text-[color:var(--color-ink-strong)]">{value}</p>
        {badge}
      </div>
    </div>
  )
}

function TextLink({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} className="mt-1 cursor-pointer text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline">
      {children}
    </button>
  )
}

function AtAGlance({ data, limits, health, usersExact, title, go }) {
  const g = data?.atAGlance ?? {}
  const failed = data?.tiles?.failedRate?.v
  const failedLevel = levelFor(failed, limits.errors)
  const opened = isNum(g.pagesOpened) ? g.pagesOpened : health?.pages
  const over = g.pagesOverLimit
  const allPages = isNum(health?.notOpened) && isNum(opened) ? opened + health.notOpened : null
  const neutral = 'var(--color-accent)'
  return (
    <PerfCard title={title} note="Four quick counts for the period, each with what it is judged against">
      <GlanceRow
        icon={XCircle}
        tone={failedLevel && failedLevel !== 'healthy' ? toneOf(failedLevel) : neutral}
        label="Opens with a failed request"
        meaning={
          <>
            {isNum(g.failedLoads) && isNum(g.loads) ? `${formatNumber(g.failedLoads)} of ${countOf(g.loads, 'open')} had at least one request to the server fail: a server error (5xx), a timeout (408), too many requests (429) or no reply — a permission or not-found reply (403, 404 …) does not count · ` : ''}
            <b>{perHundredText(failed)}</b> · {limitPerHundredText(limits.errors?.warning)}.
          </>
        }
        value={formatPercent(failed)}
        badge={
          failedLevel ? (
            failedLevel === 'healthy' ? (
              <span className="text-xs text-[color:var(--color-ink-muted)]">within the limit</span>
            ) : (
              <PerfStatusBadge status={failedLevel} />
            )
          ) : null
        }
        link={<TextLink onClick={() => go({ tab: 'pages' })}>See which pages →</TextLink>}
      />
      <GlanceRow
        icon={over > 0 ? AlertTriangle : CheckCircle2}
        tone={over > 0 ? toneOf('slow') : toneOf('healthy')}
        label="Pages over a limit"
        meaning={
          isNum(over) && isNum(opened)
            ? `${formatNumber(over)} of ${countOf(opened, 'page')} opened in the period ${over === 1 ? 'is' : 'are'} over at least one limit (load time, TTFB, LCP or failed requests).`
            : 'Not sent by the server.'
        }
        value={isNum(over) ? `${formatNumber(over)}${isNum(opened) ? ` / ${formatNumber(opened)}` : ''}` : '—'}
        badge={isNum(over) && over === 0 ? <span className="text-xs text-[color:var(--color-ink-muted)]">all within limits</span> : null}
        link={<TextLink onClick={() => go({ tab: 'pages' })}>Pages →</TextLink>}
      />
      <GlanceRow
        icon={Users}
        tone={neutral}
        label="People who opened pages"
        meaning={usersExact ? 'Different people in the period — counted, never identified.' : 'People per day, added up — the same person counts once per day, never identified.'}
        value={isNum(data?.users) ? formatNumber(data.users) : '—'}
      />
      <GlanceRow
        icon={LayoutGrid}
        tone={neutral}
        label="Pages opened"
        meaning={
          allPages != null
            ? `Pages with at least one open in the period, out of the ${formatNumber(allPages)} pages measured before. The others were not visited.`
            : 'Pages with at least one open in the period.'
        }
        value={isNum(opened) ? `${formatNumber(opened)}${allPages != null ? ` / ${formatNumber(allPages)}` : ''}` : '—'}
        link={<TextLink onClick={() => go({ tab: 'pages' })}>All pages →</TextLink>}
      />
      {g.thinData ? (
        <p className="m-0 mt-2 rounded-lg px-3 py-2 text-xs" style={{ backgroundColor: tintOf(toneOf('warning'), 12) }}>
          <b>Thin data:</b> fewer than about 20 opens in this scope — &ldquo;typical&rdquo; and &ldquo;slowest&rdquo; are simply those few opens.
        </p>
      ) : null}
      <p className="m-0 mt-2 text-xs text-[color:var(--color-ink-subtle)]">Counts follow the period and company filters above · updates live</p>
    </PerfCard>
  )
}

/* ── What is recorded ─────────────────────────────────────────────────── */

const RECORDED = [
  'Company and page (the route, with ids replaced by :id — never the query string)',
  'Timings — load time, and on full loads TTFB, FCP and LCP; CLS and INP for the visit',
  'Whether it was a full load or an in-app navigation, and whether it settled, hit the 20 s cap or was left early',
  'The page’s API calls: method, path, HTTP status, duration, first byte and reply size (up to 40 per open)',
  'Browser, operating system and device type',
  'The person — hashed per day, never identified',
]
const NEVER = [
  'Passwords and tokens',
  'Query strings, hash fragments and form values',
  'File contents and page titles',
  'The person’s id, name or email address',
  'IP addresses',
]

function WhatIsRecorded() {
  return (
    <PerfCard
      title={
        <span className="inline-flex items-center gap-2">
          <ShieldCheck size={16} className="text-[color:var(--color-success)]" /> What is recorded
        </span>
      }
      note="And what never is — for every page open, for every person, in every company"
    >
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
        <div className="min-w-0">
          <p className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-ink-subtle)]">Recorded per page open</p>
          <ul className="m-0 list-none space-y-1.5 p-0 text-sm">
            {RECORDED.map((t) => (
              <li key={t} className="flex gap-2">
                <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[color:var(--color-success)]" aria-hidden="true" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="min-w-0">
          <p className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-ink-subtle)]">Never recorded</p>
          <ul className="m-0 list-none space-y-1.5 p-0 text-sm">
            {NEVER.map((t) => (
              <li key={t} className="flex gap-2">
                <XCircle size={15} className="mt-0.5 shrink-0 text-[color:var(--color-danger)]" aria-hidden="true" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {[
          'Raw opens · 30 days',
          'Daily history · 13 months',
          'Sent after the page settles · batched · never slows a page',
          'Only signed-in company members · never Master Admins or impersonated sessions · never the sign-in screens',
        ].map((t) => (
          <span key={t} className="rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-2.5 py-1 text-xs text-[color:var(--color-ink-muted)]">
            {t}
          </span>
        ))}
      </div>
    </PerfCard>
  )
}

/* ── The tab ───────────────────────────────────────────────────────────── */

export function PerfOverviewTab({ ctx }) {
  const { scope, go, slot: fallbackSlot, companyName, openGuide, openLoadHelp } = ctx
  const query = useOverview(scope)
  const data = query.data
  const limits = useMemo(() => normaliseLimits(data?.limits), [data?.limits])
  const slot = data?.scope?.slot === 'day' || data?.scope?.slot === 'hour' ? data.scope.slot : fallbackSlot
  const where = scopePhrase(data?.scope?.organizationName ?? companyName, scope.period)
  const usersExact = usersExactFrom(data?.scope)
  const vsWhat = compareShort(scope.compare)

  const table = usePerfTable({
    defaultSort: [{ key: 'p95Ms', dir: 'desc' }],
    sizes: [5, 10, 25],
    resetKey: `${scope.organization}|${scope.period}|${scope.compare}`,
  })
  const slowest = usePages(scope, table.params)
  const columns = useMemo(
    () =>
      buildPageColumns({
        slot,
        limits,
        variant: 'overview',
        onOpen: (route) => go({ page: route }),
        onInfo: (row) => openLoadHelp({ ...row, usersExact }),
      }),
    [slot, limits, go, openLoadHelp, usersExact],
  )

  if (query.isLoading) {
    return (
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <PerfCard key={i}>
            <PanelLoading lines={3} label="Loading the overview" />
          </PerfCard>
        ))}
      </div>
    )
  }
  if (query.isError && !data) {
    return <PanelError message={`The overview ${where} could not be loaded — ${extractApiError(query.error).message}`} onRetry={() => query.refetch()} />
  }

  const t = data?.tiles ?? {}
  const p95Level = levelFor(t.p95Ms?.v, limits.load)
  const failedLevel = levelFor(t.failedRate?.v, limits.errors)
  const tz = data?.scope?.tz || viewerTimeZone()
  const chartNote =
    slot === 'day'
      ? `P95 and average per day · page loads as bars · days are UTC (today so far included)`
      : `P95 and average per hour · page loads as bars · times in ${tz}`
  const total = slowest.data?.total ?? 0
  const noOpens = (t.loads?.v ?? 0) === 0 && (data?.health?.pages ?? 0) === 0

  return (
    <div className="min-w-0 space-y-4">
      <FilterSpinner show={query.isFetching && query.isPlaceholderData} label="Loading the new filter…" />
      {noOpens ? (
        <PanelEmpty
          icon={FileSearch}
          title={`No page opens ${where}`}
          body="Pages appear here within about half a minute of someone opening them. Try a longer period or another company."
        />
      ) : null}

      <HealthStrip
        health={data?.health}
        limits={limits}
        users={data?.users}
        usersPrev={data?.usersPrev}
        usersExact={usersExact}
        compare={scope.compare}
        onStatus={(s) => go({ tab: 'pages', status: s })}
        onPages={() => go({ tab: 'pages' })}
        usersTarget={scope.organization === 'all' ? 'Companies' : 'Pages'}
        onUsers={() => go({ tab: scope.organization === 'all' ? 'organizations' : 'pages' })}
      />

      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <FigureTile
          label="Page loads"
          icon={Activity}
          value={isNum(t.loads?.v) ? formatNumber(t.loads.v) : '—'}
          meaning="Full loads and in-app navigations in the period."
          compare={<DeltaLine delta={deltaOf(t.loads?.v, t.loads?.prev, { neutral: true })} vs={isNum(t.loads?.prev) ? countOf(t.loads.prev, 'open') : vsWhat} />}
          title="How many times pages were opened in the period."
        />
        <FigureTile
          label="Average open"
          icon={Timer}
          value={formatMs(t.avgMs?.v)}
          meaning="Every open added up ÷ the opens — one very slow open pulls it up."
          compare={<DeltaLine delta={deltaOf(t.avgMs?.v, t.avgMs?.prev)} vs={isNum(t.avgMs?.prev) ? formatMs(t.avgMs.prev) : vsWhat} />}
        />
        <FigureTile
          label="Slowest 5 of 100 (P95)"
          icon={BarChart3}
          tone={p95Level ? toneOf(p95Level) : undefined}
          value={formatMs(t.p95Ms?.v)}
          info={<InfoDot onClick={() => openGuide('load')} label="What P95 means and how it is measured" />}
          meaning={`95 of 100 opens within this — the colour uses it · limits ${limitTriple(limits.load)}`}
          compare={<DeltaLine delta={deltaOf(t.p95Ms?.v, t.p95Ms?.prev)} vs={isNum(t.p95Ms?.prev) ? formatMs(t.p95Ms.prev) : vsWhat} />}
        />
        <FigureTile
          label="Opens with a failed request"
          icon={Gauge}
          tone={failedLevel ? toneOf(failedLevel) : undefined}
          value={formatPercent(t.failedRate?.v)}
          info={<InfoDot onClick={() => openGuide('err')} label="What the error rate means" />}
          meaning={`${perHundredText(t.failedRate?.v)} · ${limitPerHundredText(limits.errors?.warning)}`}
          compare={<DeltaLine delta={deltaOf(t.failedRate?.v, t.failedRate?.prev)} vs={isNum(t.failedRate?.prev) ? formatPercent(t.failedRate.prev) : vsWhat} />}
        />
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3 lg:items-stretch">
        <PerfCard className="lg:col-span-2" title="Page load" note={chartNote}>
          <LoadTimeChart
            slots={data?.chart?.slots ?? []}
            restarts={data?.chart?.restarts ?? []}
            slot={slot}
            limits={limits}
            emptyText={`No page opens ${where}`}
          />
        </PerfCard>
        <AtAGlance
          data={data}
          limits={limits}
          health={data?.health}
          usersExact={usersExact}
          title={`At a glance · ${periodLabel(scope.period)} · ${data?.scope?.organizationName ?? companyName ?? 'All companies'}`}
          go={go}
        />
      </div>

      <div className="min-w-0">
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <div className="min-w-0">
            <h3 className="m-0 font-display text-base font-semibold text-[color:var(--color-ink-strong)]">Slowest pages</h3>
            <p className="m-0 text-[13px] text-[color:var(--color-ink-muted)]">
              By the slowest 5 of 100 opens (P95) in this period · click a row to open the page
            </p>
          </div>
          <button type="button" onClick={() => go({ tab: 'pages' })} className="cursor-pointer text-sm font-medium text-[color:var(--color-accent-strong)] hover:underline">
            All pages →
          </button>
        </div>
        <DataTable
          tableId="page-perf-slowest"
          columns={columns}
          rows={slowest.data?.rows ?? []}
          getRowId={(r) => r.route}
          getRowProps={(r) => ({ onClick: () => go({ page: r.route }), className: 'cursor-pointer' })}
          loading={slowest.isLoading}
          fetching={slowest.isFetching && !slowest.isLoading}
          error={slowest.isError && !slowest.data ? extractApiError(slowest.error).message : null}
          onRetry={() => slowest.refetch()}
          search={table.q}
          onSearchChange={table.setQ}
          searchPlaceholder="Search by route"
          defaultSort={table.sort}
          onSortChange={table.setSort}
          page={table.page}
          totalPages={Math.max(1, Math.ceil(total / Math.max(1, table.limit)))}
          total={total}
          limit={table.limit}
          limitOptions={table.limitOptions}
          onPageChange={table.setPage}
          onLimitChange={table.setLimit}
          exportBaseName="page-performance-slowest-pages"
          exportTitle="Page Performance — Slowest pages"
          getExportRows={() => fetchAllPages(scope, table.params)}
          emptyState={{ icon: FileSearch, title: table.q.trim() ? `No page matches “${table.q.trim()}” ${where}` : `No page opens ${where}` }}
        />
      </div>

      <WhatIsRecorded />

      <p className="m-0 text-xs text-[color:var(--color-ink-subtle)]">
        Compared with {vsWhat}
        {data?.scope?.comparePartial ? ' (only part of that window has figures, so treat the change with care)' : ''}
        {data?.scope?.compareApproximate ? ' (approximate — whole days are compared)' : ''}.
        {isNum(data?.atAGlance?.abandoned) && data.atAGlance.abandoned > 0
          ? ` ${countOf(data.atAGlance.abandoned, 'open')} left before ${data.atAGlance.abandoned === 1 ? 'it' : 'they'} finished.`
          : ''}{' '}
        Opens left before they finished are counted on the Pages tab and kept out of every average and
        percentile; opens capped at 20 s are included — they are real slow opens. {slotUnit(slot) === 'day' ? 'Longer periods merge each day’s figures; percentiles are never averaged.' : ''}
      </p>
    </div>
  )
}
